// IMP-446 (#414) — Export 컬럼 선택 모달.
//
// 순수 프레젠테이션 컴포넌트(connect 없음). 후보 목록 구성/기본 체크값 계산은 전부
// src/lib/exportColumns.js(exportCandidateColumns / defaultExportSelection)에 위임한다.
// 이 파일은 그 결과를 렌더링하고 체크 토글·전체선택/해제·확인/취소만 담당한다
// (계산식 이중화 금지 — 프로젝트 CLAUDE.md §6).
//
// [계약 — IMP-444 Spec §5.1 규칙 4 승계 (IMP-446 핵심 가드)]
// 모달은 세션 간 선택 상태를 저장하지 않는다. showModal 이 false→true 로 바뀔 때마다
// defaultExportSelection(columns) 로 체크 상태를 다시 계산하며, 직전 선택은 버린다.
//
// [계약 요약]
// - columns 는 표시 필터를 거치지 않은 전체 컬럼 배열(숨김 컬럼 포함)이어야 한다.
// - 후보 목록/렌더 순서는 exportCandidateColumns(columns) 순서를 그대로 따른다.
// - callbackConfirm(selectedNames) 의 배열 순서는 클릭 순서가 아니라 columns 원래 순서.
// - 선택 0개면 확인 버튼 disabled + 안내 문구.
// - 원본 columns 배열/원소는 mutate 하지 않는다.
//
// [i18n — 신규 키 3개]
//   - common.label.exportColumnSelect (모달 제목 기본값)
//   - common.button.selectAll         (전체 선택 버튼)
//   - common.button.deselectAll       (전체 해제 버튼)
// 기존 키 재사용: common.button.cancel, common.button.confirm,
//   common.label.noHeadersSelected(0개 선택 안내 — 값이 '선택된 헤더 없음' 으로 그대로 들어맞는다)

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { Translate as t } from '../../config/Translate';
import { exportCandidateColumns, defaultExportSelection } from '../../lib/exportColumns';

const ExportColumnModal = ({ showModal, columns, title, callbackClose, callbackConfirm }) => {
	const candidates = useMemo(() => exportCandidateColumns(columns), [columns]);

	const [selected, setSelected] = useState(() => new Set(defaultExportSelection(columns)));
	const prevShowModalRef = useRef(false);

	// IMP-444 §5.1 규칙 4 승계 — showModal 이 false→true 로 바뀌는 순간에만 기본 체크값으로
	// 재계산한다. 모달이 열려 있는 동안의 사용자 토글은 이 effect 로 되돌리지 않는다.
	useEffect(() => {
		const justOpened = showModal && !prevShowModalRef.current;
		prevShowModalRef.current = showModal;
		if (justOpened) {
			setSelected(new Set(defaultExportSelection(columns)));
		}
	}, [showModal, columns]);

	const toggleColumn = (name) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(name)) {
				next.delete(name);
			} else {
				next.add(name);
			}
			return next;
		});
	};

	const handleSelectAll = () => {
		setSelected(new Set(candidates.map((col) => col?.name)));
	};

	const handleDeselectAll = () => {
		setSelected(new Set());
	};

	const handleConfirm = () => {
		if (selected.size === 0) return;
		const selectedNames = candidates.filter((col) => selected.has(col?.name)).map((col) => col?.name);
		callbackConfirm?.(selectedNames);
	};

	const handleClose = () => {
		callbackClose?.();
	};

	const canConfirm = selected.size > 0;

	// IMP-446 — 닫혀 있을 때는 아무것도 렌더하지 않는다. 훅 선언 뒤에 두어 showModal 전이 감지
	// effect 는 계속 살아 있다. RayTable 이 이 모달을 상시 마운트하므로, 닫힌 상태에서까지
	// react-bootstrap Modal 을 건드리면 목록 화면 전반의 렌더 경로에 불필요한 의존이 생긴다.
	if (!showModal) return null;

	return (
		<Modal show={!!showModal} size="lg" scrollable>
			<Modal.Header>
				<h2 className="modal-title">{title || t('common.label.exportColumnSelect')}</h2>
			</Modal.Header>
			<Modal.Body>
				<div className="d-flex justify-content-end mb-2">
					<Button
						variant="outline-secondary"
						size="sm"
						className="me-2"
						data-testid="export-column-select-all"
						onClick={handleSelectAll}
					>
						{t('common.button.selectAll')}
					</Button>
					<Button
						variant="outline-secondary"
						size="sm"
						data-testid="export-column-deselect-all"
						onClick={handleDeselectAll}
					>
						{t('common.button.deselectAll')}
					</Button>
				</div>
				<div className="rg-export-column-list">
					{candidates.map((col) => (
						<div key={col?.name} data-export-column-name={col?.name} className="mb-1">
							<Form.Check
								type="checkbox"
								id={`export-column-check-${col?.name}`}
								label={col?.label || col?.name}
								checked={selected.has(col?.name)}
								onChange={() => toggleColumn(col?.name)}
							/>
						</div>
					))}
				</div>
				{!canConfirm && (
					<div className="text-danger mt-2" data-testid="export-column-empty-hint">
						{t('common.label.noHeadersSelected')}
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="primary" data-testid="export-column-confirm" disabled={!canConfirm} onClick={handleConfirm}>
					{t('common.button.confirm')}
				</Button>
				<Button variant="dark" data-testid="export-column-cancel" onClick={handleClose}>
					{t('common.button.cancel')}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default ExportColumnModal;
